export type AnswerRevealResult =
    | 'done'
    | 'fallback'
    | 'not-in-quest'
    | 'not-mounted'
    | 'timeout'
    | 'cancelled'
    | 'superseded'

export type AnswerRevealIntent = {
    key: string
    questId?: string
    answerId: string
    deadlineAt: number
    instant: boolean
    contextScroll?: boolean
    highlight: boolean
    shake: boolean
    shakeQuestByAnswer: boolean
    shakeQuestId?: string
    onRevealed?: (result: AnswerRevealResult) => void
}

type ActiveAnswerReveal = {
    intent: AnswerRevealIntent
    phase: 'pending' | 'revealing'
    promise: Promise<AnswerRevealResult>
    resolve: (result: AnswerRevealResult) => void
    timer: ReturnType<typeof setTimeout> | null
}

type AnswerRevealCoordinatorOptions = {
    canReveal: (intent: AnswerRevealIntent) => boolean
    reveal: (intent: AnswerRevealIntent, shouldCancel: () => boolean) => Promise<AnswerRevealResult>
    onStart?: (intent: AnswerRevealIntent) => void
    // Fires when the intent is claimed (phase flips to 'revealing'), i.e. when visible
    // motion is about to start — unlike onStart, which fires at submission time.
    onReveal?: (intent: AnswerRevealIntent) => void
    onComplete?: (intent: AnswerRevealIntent, result: AnswerRevealResult) => void
    now?: () => number
    retryDelayMs?: number
}

/**
 * Owns the idempotency boundary for one panel's answer reveals.
 *
 * Readiness may be retried while a request is pending. Once reveal() is claimed, its
 * result is terminal: the coordinator never replays a visible reveal for that intent.
 */
export class AnswerRevealCoordinator {
    private active: ActiveAnswerReveal | null = null
    private lastEnsuredKey: string | null = null
    private lastEnsuredResult: AnswerRevealResult = 'cancelled'

    constructor(private readonly options: AnswerRevealCoordinatorOptions) {}

    request = (intent: AnswerRevealIntent): Promise<AnswerRevealResult> => {
        this.finish(this.active, 'superseded')

        let resolve!: (result: AnswerRevealResult) => void
        const promise = new Promise<AnswerRevealResult>(finish => {
            resolve = finish
        })
        const active: ActiveAnswerReveal = {
            intent,
            phase: 'pending',
            promise,
            resolve,
            timer: null,
        }

        this.active = active
        this.lastEnsuredKey = intent.key
        this.options.onStart?.(intent)
        this.schedule(active, 0)
        return promise
    }

    ensure = (intent: AnswerRevealIntent): Promise<AnswerRevealResult> => {
        if (this.lastEnsuredKey === intent.key) {
            if (this.active?.intent.key === intent.key) {
                const activeIntent = this.active.intent
                activeIntent.highlight ||= intent.highlight
                activeIntent.shake ||= intent.shake
                activeIntent.shakeQuestByAnswer ||= intent.shakeQuestByAnswer
                activeIntent.shakeQuestId ||= intent.shakeQuestId
                if (this.active.phase === 'pending') {
                    activeIntent.instant ||= intent.instant
                    activeIntent.contextScroll ||= intent.contextScroll
                    activeIntent.deadlineAt = Math.max(activeIntent.deadlineAt, intent.deadlineAt)
                }
                return this.active.promise
            }
            return Promise.resolve(this.lastEnsuredResult)
        }

        return this.request(intent)
    }

    notifyReady = () => {
        const active = this.active
        if (!active || active.phase !== 'pending') return
        this.schedule(active, 0)
    }

    cancel = () => {
        this.finish(this.active, 'cancelled')
    }

    cancelKey = (key: string) => {
        if (this.active?.intent.key === key) this.finish(this.active, 'cancelled')
        if (this.lastEnsuredKey === key) this.resetEnsureKey()
    }

    resetEnsureKey = () => {
        this.lastEnsuredKey = null
        this.lastEnsuredResult = 'cancelled'
    }

    private schedule = (active: ActiveAnswerReveal, delayMs: number) => {
        if (this.active !== active || active.phase !== 'pending') return
        if (active.timer) clearTimeout(active.timer)
        active.timer = setTimeout(() => {
            active.timer = null
            void this.attempt(active)
        }, delayMs)
    }

    private attempt = async (active: ActiveAnswerReveal) => {
        if (this.active !== active || active.phase !== 'pending') return

        const now = this.options.now?.() ?? Date.now()
        if (now >= active.intent.deadlineAt) {
            this.finish(active, 'timeout')
            return
        }

        if (!this.options.canReveal(active.intent)) {
            const retryDelayMs = this.options.retryDelayMs ?? 120
            this.schedule(active, Math.min(retryDelayMs, active.intent.deadlineAt - now))
            return
        }

        // This is the idempotency boundary. No outcome after this point may return the
        // transaction to pending or schedule another visible reveal.
        active.phase = 'revealing'
        this.options.onReveal?.(active.intent)
        // The deadline bounds readiness. Once visible motion starts, let the scroller
        // finish its bounded animation; only replacement or explicit cancellation stops it.
        const shouldCancel = () => this.active !== active

        let result: AnswerRevealResult
        try {
            result = await this.options.reveal(active.intent, shouldCancel)
        } catch (error) {
            console.warn('>>> AnswerRevealCoordinator: reveal failed', {
                answerId: active.intent.answerId,
                error,
            })
            result = 'timeout'
        }

        if (this.active !== active) return
        this.finish(active, result)
    }

    private finish = (active: ActiveAnswerReveal | null, result: AnswerRevealResult) => {
        if (!active) return
        if (active.timer) clearTimeout(active.timer)
        active.timer = null

        if (this.active === active) this.active = null
        if (this.lastEnsuredKey === active.intent.key) this.lastEnsuredResult = result

        active.resolve(result)
        this.options.onComplete?.(active.intent, result)
        active.intent.onRevealed?.(result)
    }
}
